import { DateTime } from 'luxon';
import { DATESTR_FMT, getClosedDateDescription, isRestaurantClosed, TDR } from 'tdr-common';
import { buildExploreHref } from './buildExploreHref';

export type ClosedDateDescriptionConfig = {
	title?: string;
	message?: string;
	cta?: {
		text?: string;
		url?: string;
		slug?: string;
	};
};

export type ClosedDateLinkConfig = ClosedDateDescriptionConfig & {
	cta: {
		text?: string;
		url?: string;
		slug?: string;
	};
};

function parseClosedDateJsonValue(description: string): ClosedDateDescriptionConfig | null {
	let parsed: unknown = JSON.parse(description);
	let depth = 0;

	while (typeof parsed === 'string' && depth < 3) {
		parsed = JSON.parse(parsed);
		depth += 1;
	}

	if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
		return null;
	}

	return parsed as ClosedDateDescriptionConfig;
}

export function parseClosedDateDescription(description?: string): ClosedDateDescriptionConfig | null {
	if (!description) {
		return null;
	}

	try {
		return parseClosedDateJsonValue(description);
	}
	catch {
		return null;
	}
}

export function resolveClosedDateCtaHref(
	cta: ClosedDateLinkConfig['cta'],
	currentSearch: string
): string | null {
	const slug = cta.slug?.trim();
	if (slug) {
		return buildExploreHref(slug, currentSearch);
	}

	const url = cta.url?.trim();
	return url || null;
}

export function parseClosedDateConfig(description?: string): ClosedDateLinkConfig | null {
	const parsed = parseClosedDateDescription(description);
	if (!parsed?.cta) {
		return null;
	}

	const hasSlug = !!parsed.cta.slug?.trim();
	const hasUrl = !!parsed.cta.url?.trim();
	if (!hasSlug && !hasUrl) {
		return null;
	}

	return parsed as ClosedDateLinkConfig;
}

export function getClosedDateLinkNotice(
	restaurant: Partial<TDR.Restaurant>,
	dateStr?: string
): ClosedDateLinkConfig | null {
	if (!dateStr || !restaurant?.timezone) {
		return null;
	}

	const luxonDate = DateTime.fromFormat(dateStr, DATESTR_FMT, { zone: restaurant.timezone });
	if (!luxonDate.isValid || !isRestaurantClosed(luxonDate, restaurant)) {
		return null;
	}

	const description = getClosedDateDescription(luxonDate, restaurant);
	return parseClosedDateConfig(description);
}

type ClosedDateLinkNoticeDebugInput = {
	surface: 'carousel' | 'component';
	restaurant?: Partial<TDR.Restaurant>;
	dateStr?: string;
	isLoading?: boolean;
	currentSearch?: string;
};

export function logClosedDateLinkNoticeDebug(input: ClosedDateLinkNoticeDebugInput) {
	const { restaurant, dateStr, isLoading, currentSearch = '' } = input;
	const timezone = restaurant?.timezone;
	const luxonDate = dateStr && timezone
		? DateTime.fromFormat(dateStr, DATESTR_FMT, { zone: timezone })
		: null;
	const closedDateLookupLabel = luxonDate?.isValid ? luxonDate.toFormat('MMMM dd, yyyy') : null;
	const rawDescription = luxonDate?.isValid && restaurant
		? getClosedDateDescription(luxonDate, restaurant)
		: undefined;
	const parsedDescription = parseClosedDateDescription(rawDescription);
	const linkConfig = getClosedDateLinkNotice(restaurant, dateStr);
	const resolvedHref = linkConfig ? resolveClosedDateCtaHref(linkConfig.cta, currentSearch) : null;
	const isClosed = !!(luxonDate?.isValid && restaurant && isRestaurantClosed(luxonDate, restaurant));
	const carouselGateOpen = isLoading !== undefined && !isLoading && !!linkConfig && !!resolvedHref;

	const conditions = {
		surface: input.surface,
		searchFiltersDate: dateStr ?? null,
		restaurantTimezone: timezone ?? null,
		luxonDateValid: luxonDate?.isValid ?? false,
		closedDateLookupLabel,
		configuredClosedDates: restaurant?.closed?.dates?.map((entry) => entry.date) ?? [],
		closedDateLabelMatchesConfigured: closedDateLookupLabel
			? restaurant?.closed?.dates?.some((entry) => entry.date === closedDateLookupLabel) ?? false
			: false,
		isRestaurantClosed: isClosed,
		closedWeekdays: restaurant?.closed?.weekdays ?? [],
		rawClosedDateDescription: rawDescription ?? null,
		parsedHasTitleOrMessage: !!(parsedDescription?.title || parsedDescription?.message),
		parsedHasCtaSlug: !!parsedDescription?.cta?.slug?.trim(),
		parsedHasCtaUrl: !!parsedDescription?.cta?.url?.trim(),
		resolvedCtaHref: resolvedHref,
		linkConfigResolved: !!linkConfig,
		isLoading: isLoading ?? null,
		carouselShowClosedDateLinkNotice: carouselGateOpen,
		wouldShowOnCarousel: carouselGateOpen
	};

	console.log('[ClosedDateLinkNotice debug]', conditions);
	return conditions;
}
